import React, { useRef, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import AstraAvatar from '../concierge/AstraAvatar';
import { ASK_BOX } from '../../content/collegeAsk';
import { CHAT } from '../../content/astraChat';
import { track, EVENTS } from '../../services/analytics';
import useShownOnce from '../../hooks/useShownOnce';

// "Ask Astra about <School>" (#3262; founder pick A of Set 1a of the Astra
// entry mocks, 2026-10-06): one box right after the four fact tiles, so a
// visitor from Google meets Astra at about the first screen and a half
// instead of 88% of the way down. The chat's own header, three seeds that
// name the school, and a box to ask your own. A seed or a typed question
// opens Astra as a sheet over the page (CollegePage owns the sheet), asking.
//
// Prerendered with the page; it hides when the open chat does not answer
// this visitor (the route closed, its budget spent, or a signed-in account,
// who has Astra in the app): `available` false.
//
// `college`: the page's data; `seeds`: content/collegeAsk.collegeSeeds;
// `onOpen({q, entry: 'seed'|'typed', seedId})`.
//
// A News story's box (#3272, founder pick A of the News Astra mocks) is the
// same box with the story's words: AskBox below, which the college box wraps.
export default function AskAstraBox({ college, seeds, available = true, onOpen, className = '' }) {
    const short = college.shortName || college.name;
    return (
        <AskBox idPrefix="college-ask" seeds={seeds} available={available} className={className}
            words={{ title: ASK_BOX.title(short), line: ASK_BOX.line, placeholder: ASK_BOX.placeholder(short),
                ariaLabel: ASK_BOX.ariaLabel(short), go: ASK_BOX.go }}
            foot={ASK_BOX.foot(short)}
            onShown={() => track(EVENTS.COLLEGE_ASK_SHOWN, { slug: college.slug, school_id: college.kbId || null })}
            onOpen={({ q, entry, seed }) => {
                track(EVENTS.COLLEGE_ASK_OPENED, { slug: college.slug, school_id: college.kbId || null, entry, seed_id: seed ? seed.id : null });
                onOpen?.({ q, entry, seedId: seed ? seed.id : null });
            }} />
    );
}

// The box itself. `words`: {title, line, placeholder, ariaLabel, go};
// `foot`: the line (or node) under it; `icon`: in place of Astra's mark (a
// school's crest, decoration only); `idPrefix`: the test ids and element ids;
// `onShown()`: once, when at least half the box is on screen;
// `onOpen({q, entry: 'seed'|'typed', seed})`.
export function AskBox({ idPrefix, seeds, available = true, words, foot, icon = null, onShown, onOpen, className = '' }) {
    const [draft, setDraft] = useState('');
    const ref = useRef(null);
    useShownOnce(ref, available, onShown);

    if (!available) return null;

    const submit = (e) => {
        e.preventDefault();
        const q = draft.trim();
        if (q.length < 2) return;
        setDraft('');
        onOpen?.({ q: q.slice(0, 500), entry: 'typed', seed: null });
    };

    return (
        <section ref={ref} id="ask-astra" data-testid={`${idPrefix}-box`} aria-labelledby={`${idPrefix}-title`}
            className={`${className ? `${className} ` : ''}mt-5 overflow-hidden rounded-2xl border border-[#E0DED8] bg-white shadow-sm`}>
            <div className="flex items-center gap-2 bg-[#1A4D2E] px-4 py-3">
                {icon || <AstraAvatar size={24} />}
                <div className="min-w-0 flex-1">
                    <h2 id={`${idPrefix}-title`} className="text-sm font-semibold text-white">{words.title}</h2>
                    <p className="truncate text-[11px] text-[#C7E3CB]">{words.line}</p>
                </div>
            </div>
            <div className="space-y-2 p-4">
                {seeds.length > 0 && (
                    <div className="grid gap-2 lg:grid-cols-3">
                        {seeds.map((s) => (
                            <button key={s.id} type="button" onClick={() => onOpen?.({ q: s.q, entry: 'seed', seed: s })}
                                data-testid={`${idPrefix}-seed`} data-seed={s.id}
                                className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl border border-[#CFE0D2] bg-white px-3.5 py-2.5 text-left text-sm font-medium text-[#1A4D2E] hover:bg-[#F0F7F1]">
                                <span>{s.q}</span>
                                <ArrowRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                            </button>
                        ))}
                    </div>
                )}
                <form onSubmit={submit} className="pt-1">
                    <div className="flex items-end gap-2 rounded-xl border border-[#E0DED8] bg-white py-2.5 pl-3.5 pr-2.5 focus-within:border-[#1A4D2E]">
                        <label htmlFor={`${idPrefix}-input`} className="sr-only">{words.ariaLabel}</label>
                        <input id={`${idPrefix}-input`} data-testid={`${idPrefix}-input`} type="text" value={draft}
                            onChange={(e) => setDraft(e.target.value)} maxLength={500} autoComplete="off"
                            placeholder={words.placeholder}
                            className="min-h-[40px] min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-[#2C2C2C] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-0" />
                        <button type="submit" data-testid={`${idPrefix}-go`} disabled={draft.trim().length < 2}
                            className="min-h-[40px] shrink-0 rounded-lg bg-[#1A4D2E] px-4 text-sm font-semibold text-white disabled:opacity-60">
                            {words.go || ASK_BOX.go}
                        </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-[#6B6B6B]">{CHAT.saved}</p>
                </form>
                <p className="text-xs leading-snug text-[#6B6B6B]">{foot}</p>
            </div>
        </section>
    );
}
